import { useState, useEffect, useCallback } from "react";
import { Campo } from "../../../services/tipos";
import { TipoFormulario } from "../../../services/constantes";
import { Page } from "../types";

interface UseFormStateProps {
  show: boolean;
  campos: Campo[];
  modo: TipoFormulario;
  entidadeSelecionada?: any;
  atualizacaoEntidade?: {
    forcarAtualizacao: boolean | number;
    entidadeUsadaParaAtualizacao: any;
  };
  pages?: Page[];
  pageIndex?: number;
  currentPageIndex?: (index: number) => void;
}

export function useFormState(props: UseFormStateProps) {
  const [entidade, setEntidade] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [internalCurrentPage, setInternalCurrentPage] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const currentPage = props.pageIndex !== undefined ? props.pageIndex : internalCurrentPage;

  const setCurrentPage = useCallback((page: number) => {
    if (props.currentPageIndex) {
      props.currentPageIndex(page);
    } else {
      setInternalCurrentPage(page);
    }
  }, [props.currentPageIndex]);

  // Reset page on show
  useEffect(() => {
    if (props.show && props.pageIndex === undefined) {
      setInternalCurrentPage(0);
    }
  }, [props.show, props.pageIndex]);

  // Clear errors on show
  useEffect(() => {
    if (props.show) {
      setErrors({});
      setIsDirty(false);
    }
  }, [props.show]);

  // Load entity data
  useEffect(() => {
    if (props.show) {
      let ent = props.entidadeSelecionada ? { ...props.entidadeSelecionada } : {};

      // Aplica valorPadrao dos campos no modo cadastro (para campos não preenchidos)
      if (props.modo === TipoFormulario.tpCadastro) {
        props.campos.forEach(c => {
          if (c.valorPadrao !== undefined && (ent[c.nome] === undefined || ent[c.nome] === null || ent[c.nome] === '')) {
            ent[c.nome] = c.valorPadrao;
          }
        });
      }

      setEntidade(ent);
    }
  }, [props.show, props.entidadeSelecionada]);

  // Handle external entity updates
  useEffect(() => {
    const updates: any = {};
    props.campos.forEach((c) => {
      const valor = props.atualizacaoEntidade?.entidadeUsadaParaAtualizacao[c.nome];
      if (valor !== undefined && valor !== null) {
        updates[c.nome] = valor;
      }
    });
    if (Object.keys(updates).length > 0) {
      setEntidade((prev: any) => ({ ...prev, ...updates }));
    }
  }, [props.atualizacaoEntidade?.forcarAtualizacao]);

  const isISODateWithZeroTime = useCallback((value: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}T00:00:00(\.0+)?Z?$/.test(value);
  }, []);

  const getFieldValue = useCallback((campo: string) => {
    const value = entidade[campo];
    if (typeof value !== "boolean" && value !== 0 && !value) return "";
    if (typeof value === "string" && isISODateWithZeroTime(value)) {
      return value.slice(0, 10);
    }
    return value;
  }, [entidade, isISODateWithZeroTime]);

  const handleInputChange = useCallback((campo: string, valor: string | number | boolean) => {
    setEntidade((prevEntidade: any) => ({
      ...prevEntidade,
      [campo]: valor,
    }));
    setIsDirty(true);
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (valor !== "" && valor !== null && valor !== undefined) {
        delete newErrors[campo];
      }
      return newErrors;
    });
  }, []);

  const handleBlur = useCallback((campo: Campo, value?: any) => {
    campo.onCampoDigitar && campo.onCampoDigitar(getFieldValue(campo.nome));
    campo.onCampoSair && campo.onCampoSair(getFieldValue(campo.nome));
    const fieldValue = value !== undefined ? value : getFieldValue(campo.nome);

    if (campo.required && props.modo !== TipoFormulario.tpVisualizacao) {
      if (fieldValue === "" || fieldValue === null || fieldValue === undefined || (campo.tipo === "checkbox" && fieldValue === false)) {
        setErrors(prev => ({
          ...prev,
          [campo.nome]: `"${campo.label}" é obrigatório`
        }));
        return;
      }
    }

    // Validação de data inválida
    if ((campo.tipo === "date" || campo.tipo === "datetime-local") && fieldValue === 'INVALID_DATE' && props.modo !== TipoFormulario.tpVisualizacao) {
      setErrors(prev => ({
        ...prev,
        [campo.nome]: `Data inválida`
      }));
      return;
    }

    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[campo.nome];
      return newErrors;
    });
  }, [getFieldValue, props.modo]);

  const isCampoVisivel = useCallback((campo: Campo) => {
    return typeof campo.visible === 'function'
      ? campo.visible(entidade)
      : campo.visible !== false;
  }, [entidade]);

  const validateCurrentPage = useCallback(() => {
    if (!props.pages) return true;

    const newErrors: Record<string, string> = {};
    const currentPageFields = props.pages[currentPage].campos;

    props.campos.forEach((campo: Campo) => {
      if (currentPageFields.includes(campo.nome) && isCampoVisivel(campo) && campo.tipo !== "label" && props.modo !== TipoFormulario.tpVisualizacao) {
        const value = getFieldValue(campo.nome);
        if (campo.required && (value === "" || value === null || value === undefined || (campo.tipo === "checkbox" && value === false))) {
          newErrors[campo.nome] = `"${campo.label}" é obrigatório`;
        }
        if ((campo.tipo === "date" || campo.tipo === "datetime-local") && value === 'INVALID_DATE') {
          newErrors[campo.nome] = `Data inválida`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [props.campos, props.pages, currentPage, getFieldValue, props.modo, isCampoVisivel]);

  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    props.campos.forEach((campo: Campo) => {
      if (isCampoVisivel(campo) && campo.tipo !== "label") {
        const value = getFieldValue(campo.nome);
        if (campo.required && (value === "" || value === null || value === undefined || (campo.tipo === "checkbox" && value === false))) {
          newErrors[campo.nome] = `"${campo.label}" é obrigatório`;
        }
        if ((campo.tipo === "date" || campo.tipo === "datetime-local") && value === 'INVALID_DATE') {
          newErrors[campo.nome] = `Data inválida`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [props.campos, getFieldValue, isCampoVisivel]);

  const resetForm = useCallback(() => {
    setEntidade({});
    setErrors({});
    setIsDirty(false);
    setCurrentPage(0);
  }, [setCurrentPage]);

  return {
    entidade,
    setEntidade,
    errors,
    setErrors,
    currentPage,
    setCurrentPage,
    isDirty,
    getFieldValue,
    handleInputChange,
    handleBlur,
    validateCurrentPage,
    validateForm,
    resetForm,
  };
}
